
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import HistoryCategoryForm from "./HistoryCategoryForm";
import HistoryItemForm from "./HistoryItemForm";
import HistoryQuizForm from "./HistoryQuizForm";

type HistoryCategory = Tables<"history_categories">;
type HistoryItem = Tables<"history_items">;
type HistoryQuiz = Tables<"history_quiz_questions">;

const HistoryManager = () => {
  const [categories, setCategories] = useState<HistoryCategory[]>([]);
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [quizzes, setQuizzes] = useState<HistoryQuiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [showQuizForm, setShowQuizForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<HistoryCategory | null>(null);
  const [editingItem, setEditingItem] = useState<HistoryItem | null>(null);
  const [editingQuiz, setEditingQuiz] = useState<HistoryQuiz | null>(null);
  const [selectedItemCategory, setSelectedItemCategory] = useState<string>("all");
  const [selectedQuizCategory, setSelectedQuizCategory] = useState<string>("all");
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesResult, itemsResult, quizzesResult] = await Promise.all([
        supabase.from("history_categories").select("*").order("display_order"),
        supabase.from("history_items").select("*").order("display_order"),
        supabase.from("history_quiz_questions").select("*").order("display_order")
      ]);

      if (categoriesResult.error) throw categoriesResult.error;
      if (itemsResult.error) throw itemsResult.error;
      if (quizzesResult.error) throw quizzesResult.error;

      setCategories(categoriesResult.data || []);
      setItems(itemsResult.data || []);
      setQuizzes(quizzesResult.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Error",
        description: "Failed to fetch history content",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (table: string, id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from(table as any)
        .update({ is_active: !isActive })
        .eq("id", id);

      if (error) throw error;

      await fetchData();
      toast({
        title: "Success",
        description: `Item ${!isActive ? "activated" : "deactivated"} successfully`
      });
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const deleteItem = async (table: string, id: string) => {
    if (!confirm("Are you sure you want to delete this item?")) {
      return;
    }

    try {
      const { error } = await supabase
        .from(table as any)
        .delete()
        .eq("id", id);

      if (error) throw error;

      await fetchData();
      toast({
        title: "Success",
        description: "Item deleted successfully"
      });
    } catch (error) {
      console.error("Error deleting item:", error);
      toast({
        title: "Error",
        description: "Failed to delete item",
        variant: "destructive"
      });
    }
  };

  const filteredItems = selectedItemCategory === "all" 
    ? items 
    : items.filter(item => item.category_id === selectedItemCategory);

  const filteredQuizzes = selectedQuizCategory === "all" 
    ? quizzes 
    : quizzes.filter(quiz => quiz.category_id === selectedQuizCategory);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-fredoka font-bold text-tinyminds-purple">
          History Content Management
        </h2>
      </div>

      <Tabs defaultValue="categories" className="space-y-6">
        <TabsList>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="items">History Items</TabsTrigger>
          <TabsTrigger value="quizzes">Quiz Questions</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Categories ({categories.length})</h3>
            <Button onClick={() => setShowCategoryForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <Card key={category.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{category.name}</CardTitle>
                    <Badge variant={category.is_active ? "default" : "secondary"}>
                      {category.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingCategory(category);
                        setShowCategoryForm(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleStatus("history_categories", category.id, category.is_active!)}
                    >
                      {category.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteItem("history_categories", category.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="items" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">History Items ({filteredItems.length})</h3>
            <div className="flex gap-2 items-center">
              <Select value={selectedItemCategory} onValueChange={setSelectedItemCategory}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => setShowItemForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredItems.map((item) => {
              const category = categories.find(c => c.id === item.category_id);
              return (
                <Card key={item.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{item.name}</CardTitle>
                      <Badge variant={item.is_active ? "default" : "secondary"}>
                        {item.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      Category: {category?.name}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingItem(item);
                          setShowItemForm(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleStatus("history_items", item.id, item.is_active!)}
                      >
                        {item.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteItem("history_items", item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="quizzes" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Quiz Questions ({filteredQuizzes.length})</h3>
            <div className="flex gap-2 items-center">
              <Select value={selectedQuizCategory} onValueChange={setSelectedQuizCategory}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => setShowQuizForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredQuizzes.map((quiz) => {
              const category = categories.find(c => c.id === quiz.category_id);
              return (
                <Card key={quiz.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <Badge variant={quiz.is_active ? "default" : "secondary"}>
                        {quiz.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      Category: {category?.name} | Level: {quiz.difficulty_level}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm mb-3 line-clamp-2">{quiz.question}</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingQuiz(quiz);
                          setShowQuizForm(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleStatus("history_quiz_questions", quiz.id, quiz.is_active!)}
                      >
                        {quiz.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteItem("history_quiz_questions", quiz.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {showCategoryForm && (
        <HistoryCategoryForm
          category={editingCategory}
          onClose={() => {
            setShowCategoryForm(false);
            setEditingCategory(null);
          }}
          onSuccess={() => {
            fetchData();
            setShowCategoryForm(false);
            setEditingCategory(null);
          }}
        />
      )}

      {showItemForm && (
        <HistoryItemForm
          item={editingItem}
          categories={categories}
          onClose={() => {
            setShowItemForm(false);
            setEditingItem(null);
          }}
          onSuccess={() => {
            fetchData();
            setShowItemForm(false);
            setEditingItem(null);
          }}
        />
      )}

      {showQuizForm && (
        <HistoryQuizForm
          quiz={editingQuiz}
          categories={categories}
          onClose={() => {
            setShowQuizForm(false);
            setEditingQuiz(null);
          }}
          onSuccess={() => {
            fetchData();
            setShowQuizForm(false);
            setEditingQuiz(null);
          }}
        />
      )}
    </div>
  );
};

export default HistoryManager;
